import React from "react";
import Icons from "../icons";

/* ------------------------------------------------------------------ */
/*  Error boundary (FE-02)                                              */
/*                                                                     */
/*  A render error in one screen used to blank the whole app, mid-run   */
/*  or mid-delete. MainPage wraps each screen in one of these, and App  */
/*  wraps MainPage, so the header and sidebar stay usable while only    */
/*  the broken screen shows the fallback card.                          */
/*                                                                     */
/*  - "Reload this screen" clears the error and mounts the screen anew. */
/*  - `resetKey`: when it changes, a boundary that has caught an error  */
/*    clears it (MainPage passes `nav`, so leaving and coming back to a */
/*    kept-mounted screen tries it again). A healthy screen is left as  */
/*    it is: nothing is remounted.                                      */
/*  - The error goes to the console; to the API later (REL-10).         */
/* ------------------------------------------------------------------ */

export const FALLBACK_TITLE = "Something went wrong on this screen";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error: error || new Error("Unknown error") };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ""}]`,
      error,
      info?.componentStack || ""
    );
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.reset();
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const message = error?.message || String(error);
    return (
      <div role="alert" className="mx-auto my-6 w-full max-w-2xl rounded-md border border-slate-200 bg-card shadow-sm">
        <div className="flex gap-4 px-5 py-5">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-rose-50 text-rose-600">
            <Icons.Alert className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1 pt-1">
            <h2 className="text-base font-semibold text-slate-800">{FALLBACK_TITLE}</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              {this.props.name ? `${this.props.name} stopped working. ` : "This part of the page stopped working. "}
              The rest of the app still works. Reload this screen to try again.
            </p>
            <details className="mt-3 text-sm text-slate-600">
              <summary className="cursor-pointer select-none font-medium text-slate-700">Error details</summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-700">
                {message}
              </pre>
            </details>
          </div>
        </div>
        <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3.5">
          <button
            type="button"
            onClick={this.reset}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
          >
            Reload this screen
          </button>
        </div>
      </div>
    );
  }
}
